import { initials } from '@gbv/utils';
import { cn } from './cn.js';

export interface AvatarProps {
  name: string;
  src?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-14 w-14 text-lg',
} as const;

/**
 * Deterministic tint per person, so the same colleague is the same colour on
 * every screen and the eye learns them.
 */
const TINTS = [
  'bg-go-light text-go-dark',
  'bg-bee-light text-bee-dark',
  'bg-vir-light text-vir-dark',
  'bg-info-light text-info',
];

function tintFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) % 997;
  return TINTS[hash % TINTS.length]!;
}

export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={cn('rounded-full object-cover', SIZES[size], className)}
      />
    );
  }

  return (
    <span
      aria-label={name}
      title={name}
      className={cn(
        'inline-grid select-none place-items-center rounded-full font-semibold',
        SIZES[size],
        tintFor(name),
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}
